import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import ContentNoChild from './ContentNoChild';

import ContentWithChild from './ContentWithChild';

const propTypes = {
  setList: PropTypes.func.isRequired,
  setDirtyList: PropTypes.func.isRequired,
  list: PropTypes.objectOf(PropTypes.object).isRequired,
  dirtyList: PropTypes.objectOf(PropTypes.object).isRequired,
  isDirty: PropTypes.bool.isRequired,
  numItems: PropTypes.number.isRequired,
  setNumItemsSelected: PropTypes.func.isRequired,
  hasChild: PropTypes.bool.isRequired,
  testId: PropTypes.string
};

const defaultProps = {
  testId: undefined
};

const Content = ({
  setList,
  setDirtyList,
  list,
  dirtyList,
  isDirty,
  numItems,
  setNumItemsSelected,
  hasChild,
  shouldNotTranslate,
  testId
}) => {
  const isSelected = collection => {
    return collection.children.some(item => item.isSelected);
  };

  const updateNumElementsSelected = useCallback(
    (isChecked, noElements) => {
      if (isChecked) {
        setNumItemsSelected(prevState => prevState + noElements);
      } else {
        setNumItemsSelected(prevState => prevState - noElements);
      }
    },
    [setNumItemsSelected]
  );

  const toggleCheckBoxChild = (e, id, childIndex) => {
    const isChecked = e?.target?.checked;
    const setFn = isDirty ? setDirtyList : setList;

    setFn(prevState => {
      const modifiedState = {
        ...prevState,
        [id]: {
          ...prevState[id],
          children: [
            ...prevState[id].children.slice(0, childIndex),
            {
              ...prevState[id].children[childIndex],
              isSelected: isChecked
            },
            ...prevState[id].children.slice(childIndex + 1)
          ]
        }
      };

      const selected = isSelected(modifiedState[id]);
      modifiedState[id].isSelected = selected;

      if (isDirty) {
        setList(state => {
          return {
            ...state,
            [id]: {
              ...state[id],
              children: state[id].children.map(child => {
                let childCopy = child;
                const modifiedDisplayName =
                  modifiedState[id].children[childIndex].displayName;

                if (childCopy.displayName === modifiedDisplayName) {
                  childCopy = { ...modifiedState[id].children[childIndex] };
                }

                return childCopy;
              }),
              isSelected: isSelected({ ...state[id], ...modifiedState[id] })
            }
          };
        });
      }

      return modifiedState;
    });

    updateNumElementsSelected(isChecked, 1);
  };

  const toggleCheckBox = useCallback(
    (e, id) => {
      const isChecked = e?.target?.checked;
      const setFn = isDirty ? setDirtyList : setList;

      setFn(prevState => {
        const modifiedState = {
          ...prevState,
          [id]: {
            ...prevState[id],
            isSelected: isChecked
          }
        };

        if (isDirty) {
          setList(state => {
            return {
              ...state,
              [id]: {
                ...modifiedState[id]
              }
            };
          });
        }

        return modifiedState;
      });

      updateNumElementsSelected(isChecked, 1);
    },
    [isDirty, setDirtyList, setList, updateNumElementsSelected]
  );

  const selectOnlyChild = useCallback(
    (e, id, childIndex) => {
      const setFn = isDirty ? setDirtyList : setList;

      setFn(prevState => {
        const modifiedState = {
          ...prevState,
          [id]: {
            ...prevState[id],
            children: prevState[id].children.map((item, index) => {
              const itemCopy = item;
              if (childIndex !== index) {
                itemCopy.isSelected = false;
              } else {
                itemCopy.isSelected = true;
              }

              return itemCopy;
            }),
            isSelected: true
          }
        };

        if (isDirty) {
          setList(state => {
            return {
              ...state,
              [id]: {
                ...state[id],
                children: state[id].children.map(child => {
                  const childCopy = child;

                  const modifiedDisplayName =
                    modifiedState[id].children[childIndex].displayName;

                  if (childCopy.displayName === modifiedDisplayName) {
                    childCopy.isSelected = true;
                  } else {
                    childCopy.isSelected = false;
                  }

                  return childCopy;
                }),
                isSelected: modifiedState[id].isSelected
              }
            };
          });
        }

        return modifiedState;
      });

      updateNumElementsSelected(false, list[id].children.length - 1);
    },
    [isDirty, list, setDirtyList, setList, updateNumElementsSelected]
  );

  const selectOnly = selectedId => {
    const setFn = isDirty ? setDirtyList : setList;

    setFn(prevState => {
      const modifiedState = {};

      Object.keys(prevState).forEach(id => {
        modifiedState[id] = { ...prevState[id] };
        if (id === selectedId) {
          modifiedState[id].isSelected = true;
        } else {
          modifiedState[id].isSelected = false;
        }
      });

      if (isDirty) {
        setList(state => {
          const stateCopy = { ...state };

          Object.keys(state).forEach(id => {
            if (id !== selectedId) {
              stateCopy[id].isSelected = false;
            } else {
              stateCopy[id].isSelected = true;
            }
          });

          return stateCopy;
        });
      }

      return modifiedState;
    });

    updateNumElementsSelected(false, numItems - 1);
  };

  const toggleCheckBoxParent = useCallback(
    (e, id) => {
      const isChecked = e?.target?.checked;
      const setFn = isDirty ? setDirtyList : setList;

      setFn(prevState => {
        const modifiedState = {
          ...prevState,
          [id]: {
            ...prevState[id],
            children: prevState[id].children.map(item => {
              const itemCopy = item;
              itemCopy.isSelected = isChecked;
              return itemCopy;
            }),
            isSelected: isChecked
          }
        };

        if (isDirty) {
          setList(state => {
            return {
              ...state,
              [id]: {
                ...state[id],
                children: state[id].children.map(child => {
                  const childCopy = child;
                  childCopy.isSelected = isChecked;
                  return childCopy;
                }),
                isSelected: isChecked
              }
            };
          });
        }

        return modifiedState;
      });

      updateNumElementsSelected(isChecked, list[id].children.length);
    },
    [isDirty, list, setDirtyList, setList, updateNumElementsSelected]
  );

  const compareFn = useCallback(
    (a, b) => {
      const nameA = list[a].displayName.toUpperCase(); // ignore upper and lowercase
      const nameB = list[b].displayName.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      return 0;
    },
    [list]
  );

  return (
    <>
      {hasChild && (
        <ContentWithChild
          list={list}
          dirtyList={dirtyList}
          toggleCheckBoxParent={toggleCheckBoxParent}
          toggleCheckBox={toggleCheckBoxChild}
          selectOnly={selectOnlyChild}
          isDirty={isDirty}
          compareFn={compareFn}
          shouldNotTranslate={shouldNotTranslate}
          testId={testId}
        />
      )}
      {!hasChild && (
        <ContentNoChild
          list={list}
          dirtyList={dirtyList}
          toggleCheckBox={toggleCheckBox}
          selectOnly={selectOnly}
          isDirty={isDirty}
          compareFn={compareFn}
          shouldNotTranslate={shouldNotTranslate}
          testId={testId}
        />
      )}
    </>
  );
};

Content.propTypes = propTypes;
Content.defaultProps = defaultProps;

export default Content;
